import React, { useState } from "react";
import { PlayInsertionType } from "@prestosports/prestostats-core/Football";
import { DateUtils } from "@prestosports/prestostats-core/Common";
import Box from "@material-ui/core/Box";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import Menu from "@material-ui/core/Menu";
import { MenuItemAction } from "@prestosports/commons-ui";
import TrashCan32 from "@carbon/icons-react/lib/trash-can/32";
import TrashCan16 from "@carbon/icons-react/lib/trash-can/16";
import Insert32 from "@carbon/icons-react/lib/insert/32";
import Edit32 from "@carbon/icons-react/lib/edit/32";
import Button from "@material-ui/core/Button";
import Collapse from "@material-ui/core/Collapse";
import classNames from "classnames";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Accordion from "@material-ui/core/Accordion";
import { Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import commonStyles from "../../common/styles/Common.styles";
import playListStyles from "../styles/PlayList.styles";
import PlayListItem from "./PlayListItem";
import { useFootballContext } from "../../../state/football-play/context";
import { useAppContext } from "../../../state/app/context";
import DeleteConfirmationDialog from "../../common/components/DeleteConfirmationDialog";
import { useGameContextContext } from "../../../state/game-context/context";
import {
	getFormattedTime,
	getPlayNumber,
	buildDriveInfo
} from "./utils/PlayUtils";
import { useStreamContext } from "../../../state/stream/context";
import { useEventContext } from "../../../state/events/context";
import { groupBy } from "../../../util/array-util";
import { getSafeOrDefault } from "../../../util/object-util";
import NumberFormat from "../../../util/number-format";
import VirtualizedList from "../../common/components/VirtualizedList";

const playActionType = "play-action";
const playStreamType = "play-stream";

const PlayListContent = ({ plays, setMenuTarget, driveInfoById = {} }) => {
	const commonClasses = commonStyles();
	const classes = playListStyles();

	const { state: eventState } = useEventContext();
	const currentEvent = eventState.currentEvent();
	const canEditCurrentEvent = eventState.canEditCurrentEvent();

	const { state: streamState } = useStreamContext();
	const streams = streamState.streams();

	const getLinkedMarkers = (play) => {
		let linkedMarkers = [];
		streams
			.filter((s) => s.eventId === currentEvent.id)
			.forEach(({ id: streamId, name: streamName, markers }) => {
				const foundMarker = markers.find((marker) => marker.playId === play.id);
				if (foundMarker)
					linkedMarkers = [
						...linkedMarkers,
						{ ...foundMarker, streamId, streamName }
					];
			});

		return linkedMarkers;
	};

	const { state: contextState } = useGameContextContext();
	const gameConfig = contextState.gameConfig();

	const { state: appState } = useAppContext();
	const editPlay = appState.editPlay();
	const insertPlay = appState.insertPlay();

	const { state } = useFootballContext();
	const searchText = state.searchText();

	const isDisabled = (p) => {
		return (
			!canEditCurrentEvent ||
			(!p.isEditable && !p.isDeletable) ||
			editPlay?.id ||
			insertPlay?.id
		);
	};

	const rowRenderer = (play) => {
		const linkedMarkers = getLinkedMarkers(play);
		const driveInfo = driveInfoById[play.id] || {};

		return (
			<PlayListItem
				actionDisabled={isDisabled(play)}
				play={play}
				searchText={searchText}
				key={play.id}
				showDriveStart={driveInfo.showDriveStart}
				driveTotalTime={driveInfo.driveTotalTime}
				onShowAction={(e) =>
					setMenuTarget({
						target: e.currentTarget,
						play,
						type: playActionType
					})
				}
				formattedTime={getFormattedTime(play.time, gameConfig)}
				onShowStreamMarkers={(e) =>
					setMenuTarget({
						target: e.currentTarget,
						play,
						type: playStreamType,
						linkedMarkers
					})
				}
				streamMarkersDisabled={linkedMarkers.length === 0}
			/>
		);
	};

	return (
		<VirtualizedList
			className={classNames(classes.root, commonClasses.scrollableContent)}
			items={plays}
			rowRenderer={rowRenderer}
			labelledby="play-list-subheader"
		/>
	);
};

const PlayListContentGrouped = ({ plays, driveInfoById = {} }) => {
	const commonClasses = commonStyles();
	const classes = playListStyles();

	const { state: eventState } = useEventContext();
	const currentEventSettings = eventState.currentEventSettings();
	const periodsInRegulation = getSafeOrDefault(
		() => currentEventSettings.gameConfiguration.general.periodsInRegulation
	);

	const groupedPlaysByPeriod = groupBy(plays, "period");

	return (
		<div className={classNames(classes.root, commonClasses.scrollableContent)}>
			{Object.keys(groupedPlaysByPeriod)
				.sort((a, b) => b - a)
				.map((period) => {
					return (
						<Accordion
							defaultExpanded
							classes={{ root: classes.accordion, expanded: classes.expanded }}
						>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								classes={{
									root: classes.accordionSummary,
									content: classes.accordionSummaryContent,
									expanded: classes.expanded
								}}
							>
								<Typography
									style={{
										color: "#fff",
										fontWeight: "bold",
										fontSize: 20,
										fontFamily: "Overpass",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap"
									}}
								>
									{NumberFormat.formatPeriod(period, periodsInRegulation)}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<PlayListContent
									plays={groupedPlaysByPeriod[period]}
									driveInfoById={driveInfoById}
								/>
							</AccordionDetails>
						</Accordion>
					);
				})}
		</div>
	);
};

const PlayList = ({ groupByPeriod = false }) => {
	const classes = playListStyles();

	const { state: appState } = useAppContext();
	const insertPlay = appState.insertPlay();
	const insertType = appState.insertType();
	const isSubmitting = appState.isSubmitting();

	const {
		state,
		applyFilter,
		deletePlay,
		toggleInsertPlayMode,
		triggerNewPeriodDialog
	} = useFootballContext();
	const filteredPlays = state.filteredPlays();
	const searchText = state.searchText();

	const driveInfoById = buildDriveInfo(filteredPlays);

	const { setTimeSkip } = useStreamContext();

	const [menuTarget, setMenuTarget] = useState(null);
	const [deleteConfirmation, setDeleteConfirmation] = useState(false);

	const confirmDelete = () => {
		deletePlay(menuTarget.play.id);
		setDeleteConfirmation(false);
		setMenuTarget(null);
	};

	const cancelDelete = () => {
		setDeleteConfirmation(false);
	};

	const playNumber = insertPlay ? getPlayNumber(insertPlay.index) : null;
	const formattedInsertType = `${insertType
		.slice(0, 1)
		.toUpperCase()}${insertType.slice(1)}`;
	const getPlayInsertionText = () => {
		if (insertType === PlayInsertionType.Replace) {
			return `Editing Play ${playNumber ? `/ #${playNumber}` : ""}`;
		}
		return `Inserting Play ${
			playNumber ? `/ ${formattedInsertType} #${playNumber}` : ""
		}`;
	};

	return (
		<>
			<TextField
				onChange={(e) => applyFilter(e.target.value)}
				variant="outlined"
				type="search"
				fullWidth
				color="secondary"
				className={classes.search}
				InputProps={{
					classes: {
						root: classes.searchField
					},

					endAdornment: !searchText ? (
						<InputAdornment position="end">
							<SearchIcon />
						</InputAdornment>
					) : null
				}}
			/>

			<Collapse
				unmountOnExit
				in={!!insertPlay}
				timeout={{
					appear: 200,
					enter: 200,
					exit: 200
				}}
			>
				<Box className={classes.insertPlayHeader}>
					<Button
						startIcon={<TrashCan16 />}
						onClick={() => toggleInsertPlayMode(null)}
						fullWidth
						color="primary"
						size="small"
					>
						{getPlayInsertionText()}
					</Button>
				</Box>
			</Collapse>
			{!filteredPlays.length && (
				<ListItem button key="emptyPlays" className={classes.noPlays}>
					<ListItemText
						primary={searchText ? "No matching items" : "No plays added so far."}
					/>
				</ListItem>
			)}
			{groupByPeriod ? (
				<PlayListContentGrouped
					plays={filteredPlays}
					driveInfoById={driveInfoById}
				/>
			) : (
				<PlayListContent
					plays={filteredPlays}
					setMenuTarget={setMenuTarget}
					driveInfoById={driveInfoById}
				/>
			)}
			<Menu
				id="playlist-item-menu"
				anchorEl={menuTarget && menuTarget.target}
				keepMounted
				classes={{ list: classes.actionMenu }}
				open={!!menuTarget}
				onClose={() => setMenuTarget(null)}
				transformOrigin={{
					vertical: -50,
					horizontal: "right"
				}}
			>
				<Box>
					{menuTarget?.type === playStreamType && (
						<>
							{menuTarget.linkedMarkers.map((linkedMarker) => {
								return (
									<MenuItemAction
										onClick={() => {
											setTimeSkip(linkedMarker.seconds, linkedMarker.streamId);
											setMenuTarget(null);
										}}
										label={
											<ListItemText
												primary={<strong>{linkedMarker.streamName}</strong>}
												secondary={
													<>
														<strong>Timestamp - </strong>
														<span>
															{DateUtils.formatMilliseconds(linkedMarker.seconds * 1000, [
																"hours",
																"minutes",
																"seconds"
															])}
														</span>
													</>
												}
											/>
										}
									/>
								);
							})}
						</>
					)}
					{menuTarget?.type === playActionType && (
						<>
							{menuTarget?.play.canEditKickReceiving && (
								<MenuItemAction
									label="Edit"
									icon={<Edit32 />}
									onClick={() => {
										triggerNewPeriodDialog(true);
										setMenuTarget(null);
									}}
								/>
							)}
							{menuTarget?.play.canReplace && (
								<MenuItemAction
									label="Edit"
									icon={<Edit32 />}
									onClick={() => {
										toggleInsertPlayMode(menuTarget.play.id, PlayInsertionType.Replace);
										setMenuTarget(null);
									}}
								/>
							)}
							{/* {menuTarget?.play.isEditable && ( */}
							{/* 	<MenuItemAction */}
							{/* 		label="Edit" */}
							{/* 		icon={<Edit32 />} */}
							{/* 		onClick={() => { */}
							{/* 			toggleEditPlayMode(menuTarget.play); */}
							{/* 			setMenuTarget(null); */}
							{/* 		}} */}
							{/* 	/> */}
							{/* )} */}
							{menuTarget?.play.isDeletable && (
								<MenuItemAction
									label="Delete"
									icon={<TrashCan32 />}
									onClick={() => setDeleteConfirmation(true)}
								/>
							)}
							{menuTarget?.play.canInsertAfter && (
								<MenuItemAction
									label="Insert After"
									icon={<Insert32 />}
									onClick={() => {
										toggleInsertPlayMode(menuTarget?.play.id, PlayInsertionType.After);
										setMenuTarget(null);
									}}
								/>
							)}
							{menuTarget?.play.canInsertBefore && (
								<span className={classes.flippedIcon}>
									<MenuItemAction
										label="Insert Before"
										icon={<Insert32 />}
										onClick={() => {
											toggleInsertPlayMode(menuTarget?.play.id, PlayInsertionType.Before);
											setMenuTarget(null);
										}}
									/>
								</span>
							)}
						</>
					)}
				</Box>
			</Menu>

			<DeleteConfirmationDialog
				title="Delete play?"
				message="Are you sure you want to delete this play?"
				open={deleteConfirmation}
				onCancel={cancelDelete}
				onConfirm={confirmDelete}
				confirmDisabled={isSubmitting}
			/>
		</>
	);
};

export default PlayList;
